import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";

import Image from "../Image/Image";
import { isIPadProOnSafari } from "helpers/Helper";
import { useTabletDetection } from "helpers/useTabletDetection";
import { useAppContext } from "@components/Provider";

import HeaderActionButtons from "@components/HeaderPage/HeaderActionButtons";

import styles from "./OffCanvasMenu.module.css";
import NextImage from "@components/Image/NextImage";

const AlgoliaSearch = dynamic(() => import("../AlgoliaSearch/AlgoliaSearch"), {
  ssr: false,
});

const OffCanvasMenu = ({ handleLogout, slug, activeBottomNavIndex, onMenuClose }) => {
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(null);
  const [menuSource, setMenuSource] = useState(null); // 'hamburger' or 'bottom-nav'
  const prevActiveBottomNavIndex = useRef(null);
  const isHamburgerMenuOpen = useRef(false);

  // Custom click handler for server-side navigation
  const handleServerSideClick = (e, url, isExternal) => {
    if (isExternal) {
      return; // Let external links work normally
    }

    e.preventDefault(); // Prevent default Link behavior
    handleCloseMenu(); // Close menu

    // Force server-side navigation by using window.location
    window.location.href = url;
  };

  const { data, isMobile, isTablet: serverIsTablet, tokenCookie, setNewValue } = useAppContext();

  // Store latest values in refs to avoid stale closures
  const setNewValueRef = useRef(setNewValue);
  const onMenuCloseRef = useRef(onMenuClose);

  // Update refs when values change
  useEffect(() => {
    setNewValueRef.current = setNewValue;
    onMenuCloseRef.current = onMenuClose;
  }, [setNewValue, onMenuClose]);

  // Synchronize isOpen with isCanavasOpen for AlgoliaSearch
  const handleSetIsOpen = useCallback((value) => {
    if (!value) {
      // When closing the search, also close the main menu
      setIsCanvasOpen(false);
      setNewValueRef.current({ showBottomNav: true });
      setMenuSource(null);
      setActiveDropdownIndex(null);
      prevActiveBottomNavIndex.current = null;
      isHamburgerMenuOpen.current = false;
      if (onMenuCloseRef.current) {
        onMenuCloseRef.current();
      }
    }
  }, []); // No dependencies needed with refs

  // Enhanced client-side tablet detection using custom hook
  const { isTablet } = useTabletDetection(serverIsTablet, isMobile);

  // Handle bottom navigation state changes
  useEffect(() => {
    // Only respond to bottom nav if:
    // 1. It's a new value and different from previous
    // 2. The hamburger menu is not currently open
    // 3. The menu is not already opened from hamburger
    // 4. The menu is not currently open (to prevent interference with closing)
    if (
      activeBottomNavIndex !== undefined &&
      activeBottomNavIndex !== null &&
      activeBottomNavIndex !== prevActiveBottomNavIndex.current &&
      !isHamburgerMenuOpen.current &&
      menuSource !== "hamburger" &&
      !isCanvasOpen
    ) {
      prevActiveBottomNavIndex.current = activeBottomNavIndex;
      setIsCanvasOpen(true);
      setActiveDropdownIndex(activeBottomNavIndex);
      setNewValueRef.current({ showBottomNav: false });
      setMenuSource("bottom-nav");
    }
  }, [activeBottomNavIndex, menuSource, isCanvasOpen]); // Removed setNewValue from dependencies

  // Reset when menu is closed
  useEffect(() => {
    if (!isCanvasOpen) {
      setMenuSource(null);
      setActiveDropdownIndex(null);
      prevActiveBottomNavIndex.current = null;
      isHamburgerMenuOpen.current = false;
    }
  }, [isCanvasOpen]);

  const handleDropdownClick = (index) => {
    // Accordion behavior: only one item can be open at a time
    // If clicking the same item, close it. If clicking a different item, close the previous and open the new one
    if (activeDropdownIndex === index) {
      // Clicking the same item - close it
      setActiveDropdownIndex(null);
    } else {
      // Clicking a different item - close the previous and open the new one
      setActiveDropdownIndex(index);
    }
  };

  // Memoize data values to prevent unnecessary re-renders
  const navItems = useMemo(
    () => data?.SiteSettings?.site_header?.nav_items || [],
    [data?.SiteSettings?.site_header?.nav_items]
  );
  const siteName = useMemo(
    () => data?.SiteSettings?.site_name || "",
    [data?.SiteSettings?.site_name]
  );
  const siteLogo = useMemo(
    () =>
      data?.SiteSettings?.primary_site_logo_svg_black
        ? data.SiteSettings.primary_site_logo_svg_black
        : data?.SiteSettings?.primary_site_logo,
    [data?.SiteSettings?.primary_site_logo_svg_black, data?.SiteSettings?.primary_site_logo]
  );

  // Memoize filtered nav items to prevent unnecessary re-computation
  const filteredNavItems = useMemo(() => {
    return navItems.filter((item) => item.text !== "My Account");
  }, [navItems]);

  // Memoize styles to prevent unnecessary re-computation
  const menuStyles = useMemo(
    () => ({
      "--offcanvas-menu-span-after":
        slug === "/" && !isMobile && !isTablet
          ? "rgb(44, 104, 176)"
          : data?.SiteSettings?.site_header?.headerTextColor,
      "--offcanvas-menu-nav-background": data?.SiteSettings?.site_header?.headerColor,
      "--offcanvas-menu-nav-div-background": "#000",
    }),
    [
      slug,
      isMobile,
      isTablet,
      data?.SiteSettings?.site_header?.headerTextColor,
      data?.SiteSettings?.site_header?.headerColor,
    ]
  );

  const menuClasses = useCallback(
    (menuItem, key) => {
      let classes = [""];

      if (isMobile || isTablet) {
        if (menuItem && menuItem.subMenus && menuItem.subMenus.length) {
          classes.push(styles["enable__submenu_dropdown"], "enable__submenu_dropdown");
          if (activeDropdownIndex === key) {
            classes.push(styles["show__submenu"], "show__submenu");
          } else {
            classes.push(styles["hide__submenu"], "hide__submenu");
          }
        }
      } else {
        classes.push(
          styles["disable__submenu_dropdown"],
          "disable__submenu_dropdown",
          styles["show__submenu"],
          "show__submenu"
        );
      }

      return classes.join(" ");
    },
    [isMobile, isTablet, activeDropdownIndex]
  );

  const handleCloseMenu = useCallback(() => {
    // Close menu immediately for fast navigation (like ContentMenuMobile)
    setIsCanvasOpen(false);
    setMenuSource(null);
    setActiveDropdownIndex(null);
    isHamburgerMenuOpen.current = false;
    prevActiveBottomNavIndex.current = null;

    // Update context and notify parent immediately
    setNewValueRef.current({ showBottomNav: true });
    if (onMenuCloseRef.current) {
      onMenuCloseRef.current();
    }
  }, []);

  const handleCheckboxChange = useCallback((e) => {
    const isChecked = e.target.checked;
    setIsCanvasOpen(isChecked);

    if (isChecked) {
      // Opening menu from hamburger - clear any bottom nav state
      setMenuSource("hamburger");
      setActiveDropdownIndex(null);
      prevActiveBottomNavIndex.current = null;
      isHamburgerMenuOpen.current = true;
      // Update context immediately
      setNewValueRef.current({ showBottomNav: false });
    } else {
      // Closing menu - reset all state regardless of source
      setMenuSource(null);
      setActiveDropdownIndex(null);
      prevActiveBottomNavIndex.current = null;
      isHamburgerMenuOpen.current = false;
      // Update context and notify parent immediately
      setNewValueRef.current({ showBottomNav: true });
      if (onMenuCloseRef.current) {
        onMenuCloseRef.current();
      }
    }
  }, []);

  // Memoize icon rendering to prevent unnecessary re-renders
  const renderIcon = useCallback(
    (imageUrl, altText, className = styles.menuIcon, width = 20, height = 20) => {
      if (!imageUrl) return null;
      return (
        <img
          src={imageUrl}
          alt={altText}
          className={className}
          width={width}
          height={height}
          loading="lazy"
        />
      );
    },
    []
  );

  const renderLink = useCallback(
    (item) => {
      if (item.isNotALink) {
        // Custom actions use buttons
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              item?.onClick?.();
              handleCloseMenu();
            }}
            className={styles.menuButton}
            style={{
              background: "none",
              border: "none",
              color: data?.SiteSettings?.site_header?.headerTextColor || "inherit",
              cursor: "pointer",
              padding: 0,
              font: "inherit",
            }}
          >
            {renderIcon(item.imageUrl, item.text)}
            {item.text}
          </button>
        );
      } else {
        // All navigation (internal and external) uses Next.js Link for consistency
        return (
          <Link
            href={item.url}
            target={item.external ? "_blank" : undefined}
            rel={item.external ? "noopener noreferrer" : "noopener"}
            style={
              item.external ? { color: data.SiteSettings.site_header.headerTextColor } : undefined
            }
            onClick={(e) => handleServerSideClick(e, item.url, item.external)}
            prefetch={!item.external} // Only prefetch internal links
          >
            {renderIcon(item.imageUrl, item.text)}
            {item.text}
          </Link>
        );
      }
    },
    [
      data?.SiteSettings?.site_header?.headerTextColor,
      handleCloseMenu,
      renderIcon,
      handleServerSideClick,
    ]
  );

  // Helper function to detect if a menu item has 3 levels of nesting
  const hasThreeLevels = useCallback((menuItem) => {
    if (!menuItem.subMenus || !menuItem.subMenus.length) return false;

    // Check if any submenu item has further submenus or children
    return menuItem.subMenus.some(
      (subItem) =>
        (subItem.subMenus && subItem.subMenus.length > 0) ||
        (subItem.children && subItem.children.length > 0)
    );
  }, []);

  // Memoize menu item rendering to prevent unnecessary re-renders
  const renderMenuItem = useCallback(
    (menuItem, key) => {
      const hasSubmenu = menuItem && menuItem.subMenus && menuItem.subMenus.length > 0;
      const isOpen = activeDropdownIndex === key;
      // When opened from bottom navigation, only show the selected item and hide others
      const shouldHideFromBottomNav =
        menuSource === "bottom-nav" && activeDropdownIndex !== null && key !== activeDropdownIndex;
      // Hide parent menu items below the expanded accordion to make room for submenu
      const shouldHideForAccordion =
        menuSource === "hamburger" && activeDropdownIndex !== null && key > activeDropdownIndex;
      const shouldHide = shouldHideFromBottomNav || shouldHideForAccordion;

      return (
        <li
          className={[menuClasses(menuItem, key), shouldHide ? styles.hiddenDropdown : ""].join(
            " "
          )}
          key={key}
          style={{
            display: shouldHide ? "none" : "block",
          }}
        >
          {/* Hide parent menu item when opened from bottom navigation */}
          {!(menuSource === "bottom-nav" && isOpen) && (
            <div
              className={[
                hasSubmenu ? styles.dropdownParent : "",
                isOpen ? styles.dropdownParentOpen : "",
              ].join(" ")}
              onClick={hasSubmenu ? () => handleDropdownClick(key) : undefined}
              style={{ cursor: hasSubmenu ? "pointer" : "default" }}
            >
              {hasSubmenu ? (
                // If it has submenu, show icon and text (clickable for dropdown)
                <>
                  {renderIcon(menuItem.imageUrl, menuItem.text)}
                  <span>{menuItem.text}</span>
                </>
              ) : (
                // If no submenu, render as a clickable link (icon will be in renderLink)
                <div onClick={(e) => e.stopPropagation()} style={{ width: "100%" }}>
                  {renderLink(menuItem)}
                </div>
              )}
              {hasSubmenu ? (
                <svg
                  className={styles.dropdownArrow}
                  style={{
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              ) : null}
            </div>
          )}
          {/* Use recursive submenu rendering here */}
          {hasSubmenu && isOpen ? (
            <RecursiveSubMenu
              subMenus={menuItem.subMenus}
              isThreeLevelMenu={hasThreeLevels(menuItem)}
            />
          ) : null}
        </li>
      );
    },
    [activeDropdownIndex, menuSource, menuClasses, renderIcon, renderLink, hasThreeLevels]
  );

  // Recursive submenu rendering
  const RecursiveSubMenu = ({ subMenus, isThreeLevelMenu, level = 0 }) => {
    if (!subMenus || !subMenus.length) return null;

    // Special handling for 3-level menu structure
    if (isThreeLevelMenu) {
      return (
        <ul className={[styles.threeLevelSubmenu, "threeLevelSubmenu"].join(" ")}>
          {subMenus.map((item, i) => (
            <li key={i} className={styles.threeLevelSubmenuItem}>
              <div className={styles.threeLevelFirstRow}>
                {renderIcon(item.imageUrl, item.text, styles.threeLevelFirstIcon, 22, 22)}
                {item.url ? (
                  <Link
                    href={item.url}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : "noopener"}
                    className={styles.threeLevelFirstText}
                    onClick={(e) => handleServerSideClick(e, item.url, item.external)}
                    style={
                      item.external
                        ? {
                            color: data?.SiteSettings?.site_header?.headerTextColor || "inherit",
                          }
                        : undefined
                    }
                    prefetch={!item.external} // Only prefetch internal links
                  >
                    {item.text}
                  </Link>
                ) : item.isNotALink ? (
                  <button
                    onClick={() => {
                      item?.onClick?.();
                      handleCloseMenu();
                    }}
                    className={styles.threeLevelFirstText}
                    style={{
                      background: "none",
                      border: "none",
                      color: data?.SiteSettings?.site_header?.headerTextColor || "inherit",
                      cursor: "pointer",
                      padding: 0,
                      font: "inherit",
                    }}
                  >
                    {item.text}
                  </button>
                ) : (
                  <span className={styles.threeLevelFirstText}>{item.text}</span>
                )}
              </div>
              {/* Only show divider if there are nested items */}
              {(item.subMenus && item.subMenus.length > 0) ||
              (item.children && item.children.length > 0) ? (
                <hr className={styles.threeLevelDivider} />
              ) : null}
              {/* Render submenus recursively if any */}
              {item.subMenus && item.subMenus.length > 0 ? (
                <RecursiveSubMenu
                  subMenus={item.subMenus}
                  isThreeLevelMenu={false}
                  level={level + 1}
                />
              ) : null}
              {/* Render children recursively if any */}
              {item.children && item.children.length > 0 ? (
                <RecursiveSubMenu
                  subMenus={item.children}
                  isThreeLevelMenu={false}
                  level={level + 1}
                />
              ) : null}
            </li>
          ))}
        </ul>
      );
    }

    // Default rendering for other submenus
    return (
      <ul className={[styles["submenu"], "submenu"].join(" ")}>
        {subMenus.map((item, i) => (
          <li
            key={i}
            className={hasThreeLevels({ subMenus: [item] }) ? styles.threeLevelSubmenuItem : ""}
          >
            {item.isNotALink ? (
              <button
                onClick={() => {
                  item?.onClick?.();
                  handleCloseMenu();
                }}
                className={styles.menuButton}
                style={{
                  background: "none",
                  border: "none",
                  color: data?.SiteSettings?.site_header?.headerTextColor || "inherit",
                  cursor: "pointer",
                  padding: 0,
                  font: "inherit",
                }}
              >
                {renderIcon(item.imageUrl, item.text)}
                {item.text}
              </button>
            ) : (
              <Link
                href={item.url}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : "noopener"}
                style={
                  item.external
                    ? { color: data.SiteSettings.site_header.headerTextColor }
                    : undefined
                }
                onClick={(e) => handleServerSideClick(e, item.url, item.external)}
                prefetch={!item.external} // Only prefetch internal links
              >
                {renderIcon(item.imageUrl, item.text)}
                {item.text}
              </Link>
            )}
            {/* Render submenus recursively */}
            {item.subMenus && item.subMenus.length > 0 ? (
              <RecursiveSubMenu
                subMenus={item.subMenus}
                isThreeLevelMenu={false}
                level={level + 1}
              />
            ) : null}
            {/* Render children recursively */}
            {item.children && item.children.length > 0 ? (
              <RecursiveSubMenu
                subMenus={item.children}
                isThreeLevelMenu={false}
                level={level + 1}
              />
            ) : null}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <div className={[styles["offcanvas-menu"], "offcanvas-menu"].join(" ")} style={menuStyles}>
        <input
          type="checkbox"
          id="toggle-menu"
          checked={isCanvasOpen}
          onChange={handleCheckboxChange}
        />
        <label htmlFor="toggle-menu" className="toggle-open">
          <NextImage
            ImageUrl={`${process.env.S3_IMAGE_URL}/EBB-hamburger-menu.gif`}
            alt="menu"
            width="20"
            height="20"
          />
          <p style={{ color: "#3866b3" }}>MENU</p>
        </label>
        <div
          className={[styles["canvas-overlay"], "canvas-overlay"].join(" ")}
          onClick={handleCloseMenu}
        ></div>
        <nav>
          <div>
            <Link href="/" onClick={(e) => handleServerSideClick(e, "/", false)}>
              <Image
                ImageUrl={siteLogo}
                Title={siteName}
                Alt_Text={siteName}
                className="ebb-logo"
                width="250"
                height="40"
                isLazyload={false}
              />
            </Link>
            <label htmlFor="toggle-menu" className="toggle-close">
              <span></span>
            </label>
          </div>
          <div className={styles["algSearch"]}>
            <AlgoliaSearch setIsOpen={handleSetIsOpen} showSearch={true} isMobile={isMobile} />
          </div>
          <ul>
            {filteredNavItems && filteredNavItems.length
              ? filteredNavItems.map((menuItem, key) => renderMenuItem(menuItem, key))
              : null}
          </ul>
          {/* Render My Account and Log Out at the bottom if logged in and mobile - only show when no dropdown is active */}
          {isMobile && tokenCookie && activeDropdownIndex === null ? (
            <div className={styles.offcanvasAccountSection}>
              <div style={{ display: "flex" }}>
                <Link
                  href="/account/profile"
                  className={styles.offcanvasAccountLink}
                  onClick={(e) => handleServerSideClick(e, "/account/profile", false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                  }}
                >
                  <img
                    src={`${process.env.S3_IMAGE_URL}/Vector.svg`}
                    alt="My Account Icon"
                    className={styles.offcanvasAccountIcon}
                  />
                  My Account
                </Link>
              </div>
              <button onClick={handleLogout} className={styles.offcanvasLogoutButton}>
                Log Out
              </button>
            </div>
          ) : null}

          {/* Render HeaderActionButtons at the very end for mobile and tablet */}
          {(isMobile || isIPadProOnSafari()) && activeDropdownIndex === null ? (
            <div className={styles["header-action-buttons-end"]}>
              <HeaderActionButtons
                classNames={{
                  "header-button-container": styles["header-button-container"],
                  subscribe: styles["subscribe"],
                  signin: styles["signin"],
                }}
                onMenuClose={handleCloseMenu}
              />
            </div>
          ) : null}
        </nav>
      </div>
    </>
  );
};

export default OffCanvasMenu;
